import type {
  BillingSummary,
  CreditBalance,
  CreditNote,
  CreditNoteDetail,
  Reversal,
  CreditNoteIn,
  InvoiceCredit,
  RefundIn,
  Campaign,
  CampaignIn,
  CampaignStatus,
  CrmDashboard,
  CsiCheckin,
  CsiCheckinPreview,
  GoogleCalendarStatus,
  GstSettings,
  GstSettingsIn,
  Invoice,
  InvoiceRow,
  Iso,
  Lead,
  LeadActivityKind,
  LeadDetail,
  LeadIn,
  LeadStage,
  WebStep,
  Meeting,
  MeetingIn,
  MeetingStatus,
  Money,
  Notice,
  NoticeIn,
  NoticeStatus,
  OnboardingDetail,
  OnboardingIn,
  OnboardingInvoice,
  OnboardingList,
  OnboardingStatus,
  PartnerList,
  PartnerStatus,
  Partner,
  Payment,
  PayMethod,
  Plan,
  PlanTerms,
  SourceActivity,
  SourceActivityIn,
  StaffLoginOut,
  Subscription,
} from "./types";
import type { ClientLegal, ClientList, ClientReminder, CustomerDetail, CustomerIn, CustomerList, Item, ItemIn, ItemList, MasterStatus } from "./types";
import type {
  Expense,
  ExpenseIn,
  PaymentMade,
  PaymentMadeIn,
  PurchaseBill,
  PurchaseBillDetail,
  PurchaseBillIn,
  PurchaseMeta,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseOrderIn,
  PurchaseSummary,
  RecurringBill,
  RecurringBillIn,
  RecurringExpense,
  RecurringExpenseIn,
  Vendor,
  VendorCredit,
  VendorCreditDetail,
  VendorCreditIn,
  VendorDetail,
  VendorIn,
} from "./types";

const BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

export class ApiError extends Error {
  status: number;
  /** The refusal's JSON body, when it says more than `detail` (an offline edit's 409 carries the server's copy: src/pwa/outbox-core.ts). */
  data?: unknown;
  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/*
 * The staff session is a bearer token. It is held in memory only, never in
 * localStorage or sessionStorage, so a script injected into the page cannot
 * lift it from storage. To survive a reload the CRM server also sets it in an
 * httpOnly cookie at sign-in, and the page asks GET /admin/auth/session for
 * the token back (see AuthContext).
 */
let accessToken: string | null = null;
export function setAccessToken(token: string | null) {
  accessToken = token;
}
/** For a file download, which cannot go through api(). */
export const getAccessToken = () => accessToken;

/*
 * The organization the screens work in (server/orgs.js): sent as X-Org-Id on every call. It comes with the sign-in
 * reply and is kept in this tab's sessionStorage (it is not a secret), so a reload stays in the same organization;
 * the server also remembers the one each person used last.
 */
const ORG_KEY = "crm.org";
let orgId: number | null = null;
export function setOrgId(id: number | null) {
  orgId = id;
  try {
    if (id) sessionStorage.setItem(ORG_KEY, String(id));
    else sessionStorage.removeItem(ORG_KEY);
  } catch {
    // Storage switched off: this tab keeps it in memory only.
  }
}
/** The organization this tab works in, or null before sign-in (the server then picks: the one asked for, else the last used). */
export function currentOrgId(): number | null {
  if (orgId) return orgId;
  try {
    return Number(sessionStorage.getItem(ORG_KEY)) || null;
  } catch {
    return null;
  }
}
export function orgHeaders(): Record<string, string> {
  const id = currentOrgId();
  return id ? { "X-Org-Id": String(id) } : {};
}

/*
 * Role changes apply without a reload (server/access.js accessVersion): every staff answer carries X-Access-Version, a
 * fingerprint of this person's role and access in the organization that answered. When it differs from the one the
 * session came with, "crm:access-changed" fires and AuthContext asks for the session again and re-draws. Answers from
 * another organization (an outbox item sent to where it was made) are not compared.
 */
let accessVersion: string | null = null;
export function setAccessVersion(v: string | null) {
  accessVersion = v;
}
function noteAccessVersion(res: Response, headers: Record<string, string>) {
  const v = res.headers.get("X-Access-Version");
  if (!v || !accessVersion || v === accessVersion || headers["X-Org-Id"] !== orgHeaders()["X-Org-Id"]) return;
  accessVersion = v;
  window.dispatchEvent(new Event("crm:access-changed"));
}

type Query = Record<string, string | number | boolean | null | undefined>;

function withQuery(path: string, query?: Query) {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "" && v !== false) qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

/*
 * The phone app (src/pwa) listens here: "crm:unreachable" / "crm:reachable" when the server stops or starts answering
 * (the offline banner), and the offline read cache keeps the last-viewed customers and invoices a GET returned, to answer
 * the same GET when the server cannot be reached. Nothing is kept unless the shell turned the cache on (setOfflineReads).
 */
let reachable = true;
function noteReach(ok: boolean) {
  if (ok === reachable) return;
  reachable = ok;
  window.dispatchEvent(new Event(ok ? "crm:reachable" : "crm:unreachable"));
}
type OfflineReads = { save: (path: string, data: unknown) => void; load: (path: string) => Promise<unknown> };
let offlineReads: OfflineReads | null = null;
export const setOfflineReads = (r: OfflineReads | null) => {
  offlineReads = r;
};

/*
 * Wave 9 (docs/implemented-offline9.md): every edit carries the version it was opened at. The server adds `edit_version` to
 * what the screens read (a record, each row of a list, a settings answer: server/edit-versions.js); it is kept here per
 * organization and path, and a PUT or PATCH of that path sends it back as X-Base-Version unless the caller sent one (the
 * outbox and editOrQueue do). A 409 with the server's copy then goes to the phone app's conflict screen, and an edit that
 * cannot reach the server waits in the outbox with its version (src/pwa/outbox.ts setEditHooks); either way the screen is
 * told in words, and nothing is overwritten.
 */
type Seen = { v: string; data: unknown };
const versions = new Map<string, Seen>();
export type EditOp = { method: "PUT" | "PATCH"; path: string; body: unknown; version: string; org: number; base: unknown; data?: unknown };
type EditHooks = { conflict: (op: EditOp) => Promise<string | null>; queue: (op: EditOp) => Promise<string | null> };
let editHooks: EditHooks | null = null;
export const setEditHooks = (h: EditHooks | null) => {
  editHooks = h;
};
const isPlain = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
/** A path as versions are kept: the query's parameters in order (a settings answer named by its query). */
function pathKey(path: string) {
  const [p, q] = path.split("?");
  if (!q) return p;
  const s = new URLSearchParams(q);
  s.sort();
  return `${p}?${s.toString()}`;
}
/** The record an answer object is, from the path asked (server/edit-versions.js recordPath). */
export function recordPathOf(path: string, rid: string | number) {
  const parts = path.split("?")[0].split("/");
  const at = parts.lastIndexOf(String(rid));
  return at >= 0 ? parts.slice(0, at + 1).join("/") : `${parts.join("/")}/${rid}`;
}
function noteVersions(path: string, data: unknown, org: number) {
  const keep = (p: string, v: unknown, d: unknown) => typeof v === "string" && versions.set(`${org}:${pathKey(p)}`, { v, data: d });
  const rows = (list: unknown[]) => list.forEach((r) => isPlain(r) && (typeof r.id === "string" || typeof r.id === "number") && keep(`${path.split("?")[0]}/${r.id}`, r.edit_version, r));
  if (Array.isArray(data)) return rows(data);
  if (!isPlain(data)) return;
  if (typeof data.edit_version === "string") keep(typeof data.id === "string" || typeof data.id === "number" ? recordPathOf(path, data.id) : path, data.edit_version, data);
  for (const v of Object.values(data)) if (Array.isArray(v)) rows(v);
}
/** The version this tab last saw of a path (and what it was then), in the organization asked. */
export const seenVersion = (path: string, org = currentOrgId() ?? 1) => versions.get(`${org}:${pathKey(path)}`);

/** The one way the CRM talks to the API. Never call fetch from a component. `extra` adds headers (the outbox's Idempotency-Key). */
export async function api<T>(method: string, path: string, body?: unknown, extra?: Record<string, string>): Promise<T> {
  // `extra` comes last: an outbox item names the organization it was made in (X-Org-Id), whichever one this tab is in now.
  const headers: Record<string, string> = { "X-Client": "crm", ...orgHeaders(), ...extra };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const org = Number(headers["X-Org-Id"]) || 1;
  // Wave 9: an edit names the version it was opened at; one the screen sent itself (the outbox) is handled there.
  const edit = (method === "PUT" || method === "PATCH") && !headers["X-Base-Version"] && !headers["Idempotency-Key"] ? seenVersion(path, org) : undefined;
  if (edit) headers["X-Base-Version"] = edit.v;
  const op = (data?: unknown): EditOp | null => (edit ? { method: method as "PUT" | "PATCH", path, body, version: edit.v, org, base: edit.data, data } : null);
  // Offline: the copy of this record kept when it was last viewed, if any (read-only; the banner says so).
  const fallback = async (error: ApiError): Promise<T> => {
    noteReach(false);
    const kept = method === "GET" && offlineReads ? await offlineReads.load(path).catch(() => undefined) : undefined;
    if (kept === undefined) throw error;
    noteVersions(path, kept, org); // wave 9: an edit made from a kept copy names the version it was kept at
    return kept as T;
  };
  // No server: wave 9, an edit with its version waits in the outbox (src/pwa/outbox.ts) and the screen is told so; else a GET
  // is answered from the copy kept on this device, if any.
  const unreachable = async (error: ApiError): Promise<T> => {
    const queued = op() && editHooks ? await editHooks.queue(op()!).catch((e: unknown) => (e instanceof ApiError && e.status === 422 ? e : null)) : null;
    if (queued instanceof ApiError) throw queued; // the device's own check refused it (validate-core.ts)
    if (queued) {
      noteReach(false);
      throw new ApiError(0, queued, { queued: true });
    }
    return fallback(error);
  };
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "include",
    });
  } catch {
    return unreachable(new ApiError(0, "The CRM server could not be reached. Check your connection and try again."));
  }
  if (res.status === 401 && accessToken) {
    window.dispatchEvent(new Event("crm:session-expired"));
  }
  noteAccessVersion(res, headers);
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    // The dev proxy and nginx answer 502/503/504 (or a bare 500) when
    // The CRM server is not running: say so plainly instead of a status code.
    const down = res.status >= 502 || (res.status === 500 && !(res.headers.get("content-type") ?? "").includes("json"));
    if (down) return unreachable(new ApiError(0, "The CRM server is not running. Start it with npm run dev, then try again."));
    noteReach(true);
    let body: unknown;
    try {
      const data = (await res.json()) as { detail?: unknown };
      body = data;
      if (typeof data.detail === "string") message = data.detail;
      else if (Array.isArray(data.detail)) message = "Some fields are not valid. Check the form and try again.";
    } catch {
      // Not JSON: keep the status line.
    }
    // Wave 9: someone else saved meanwhile. Both copies go to the conflict screen; the form says so.
    if (res.status === 409 && isPlain(body) && isPlain(body.conflict) && op() && editHooks) {
      const said = await editHooks.conflict(op(body)!).catch(() => null);
      if (said) message = said;
    }
    throw new ApiError(res.status, message, body);
  }
  noteReach(true);
  if (res.status === 204) return undefined as T;
  const data = (await res.json()) as T;
  if (method === "GET") offlineReads?.save(path, data);
  noteVersions(path, data, org);
  return data;
}

const get = <T,>(path: string, query?: Query) => api<T>("GET", withQuery(path, query));
const post = <T,>(path: string, body?: unknown) => api<T>("POST", path, body ?? {});
const patch = <T,>(path: string, body: unknown) => api<T>("PATCH", path, body);
const del = <T,>(path: string) => api<T>("DELETE", path);

/* ---------- existing Backend-v2 routes ---------- */

export const auth = {
  login: (email: string, password: string) => post<StaffLoginOut>("/admin/auth/login", { email, password }),
  verifyMfa: (pending_token: string, code: string) => post<StaffLoginOut>("/admin/auth/mfa/verify", { pending_token, code }),
  logout: () => api<void>("POST", "/admin/auth/logout"),
  /** After a reload: the session the server's httpOnly cookie holds, or nothing (204) if signed out. */
  session: () => api<StaffLoginOut | undefined>("GET", "/admin/auth/session"),
};

export const partners = {
  list: (q: { status?: PartnerStatus | ""; q?: string }) => get<PartnerList>("/admin/partners", q),
  get: (id: string) => get<Partner>(`/admin/partners/${id}`),
  setStatus: (id: string, status: Exclude<PartnerStatus, "pending">, reason: string | null) =>
    post<Partner>(`/admin/partners/${id}/status`, { status, reason }),
};

export const notices = {
  list: (q: { status?: NoticeStatus | "" }) => get<Notice[]>("/admin/notices", q),
  create: (body: NoticeIn) => post<Notice>("/admin/notices", body),
  publish: (id: string, publish_at: string | null) => post<Notice>(`/admin/notices/${id}/publish`, { publish_at }),
  withdraw: (id: string, reason: string) => post<Notice>(`/admin/notices/${id}/withdraw`, { reason }),
};

/*
 * Client onboarding, served by the CRM's own server. A client pays a package in two parts;
 * recording a payment emails the invoice, and the balance's payment link is emailed at
 * the date and time staff set. Every call below that emails the client answers with the
 * onboarding plus `email`: whether it went out, was saved to the outbox, or failed.
 */
export const onboarding = {
  list: (q: { status?: OnboardingStatus | "overdue" | ""; q?: string }) => get<OnboardingList>("/admin/onboarding", q),
  get: (id: string) => get<OnboardingDetail>(`/admin/onboarding/${id}`),
  create: (body: OnboardingIn) => post<OnboardingDetail>("/admin/onboarding", body),
  planTerms: () => get<PlanTerms[]>("/admin/onboarding/plan-terms"),
  setPlanTerms: (body: { plan_code: string; plan_name: string; upfront_percent: number }) => post<PlanTerms[]>("/admin/onboarding/plan-terms", body),
  cancel: (id: string, reason: string) => post<OnboardingDetail>(`/admin/onboarding/${id}/cancel`, { reason }),
  /** Owner changes the upfront share (and optionally the total) while nothing has been received or sent. */
  setSplit: (id: string, body: { upfront_percent: number; total_amount?: Money }) => post<OnboardingDetail>(`/admin/onboarding/${id}/terms`, body),
  /** Owner plans the rest of the package: any number of payments, each with its own amount and date. They must add up to what is left. */
  planBalance: (id: string, installments: { amount: Money; due_at: Iso | null }[]) => post<OnboardingDetail>(`/admin/onboarding/${id}/plan-balance`, { installments }),
  invoices: () => get<InvoiceRow[]>("/admin/onboarding/invoices"),
  recordPayment: (id: string, inst: string, body: { method: PayMethod; reference: string | null; received_on: string | null }) =>
    post<OnboardingDetail>(`/admin/onboarding/${id}/installments/${inst}/pay`, body),
  sendLink: (id: string, inst: string) => post<OnboardingDetail>(`/admin/onboarding/${id}/installments/${inst}/send-link`),
  schedule: (id: string, inst: string, due_at: string) => post<OnboardingDetail>(`/admin/onboarding/${id}/installments/${inst}/schedule`, { due_at }),
  unschedule: (id: string, inst: string) => post<OnboardingDetail>(`/admin/onboarding/${id}/installments/${inst}/unschedule`),
  invoice: (id: string, inst: string) => get<OnboardingInvoice>(`/admin/onboarding/${id}/installments/${inst}/invoice`),
  resendInvoice: (id: string, inst: string) => post<OnboardingDetail>(`/admin/onboarding/${id}/installments/${inst}/invoice/send`),
  /** Where the client is and its GSTIN, for the invoices issued from now on. */
  setTaxDetails: (id: string, body: { client_state: string | null; client_gstin: string | null }) => post<OnboardingDetail>(`/admin/onboarding/${id}/tax-details`, body),
};

// Served by the CRM's own server (server/billing.js) from its onboarding payments and price list. Only the GST settings can be changed.
export const billing = {
  gstSettings: () => get<GstSettings>("/admin/billing/gst-settings"),
  saveGstSettings: (body: GstSettingsIn) => post<GstSettings>("/admin/billing/gst-settings", body),
  summary: () => get<BillingSummary>("/admin/billing/summary"),
  invoices: (q: { status?: string; overdue?: boolean; q?: string }) => get<Invoice[]>("/admin/billing/invoices", q),
  payments: () => get<Payment[]>("/admin/billing/payments"),
  subscriptions: (q: { status?: string; expiring_within_days?: number; q?: string }) =>
    get<Subscription[]>("/admin/billing/subscriptions", q),
  plans: () => get<Plan[]>("/admin/billing/plans"),
  // Credit notes, refunds and void (server/credit-notes.js).
  creditNotes: (q: { status?: string; q?: string; client_email?: string; invoice_id?: string }) => get<CreditNote[]>("/admin/billing/credit-notes", q),
  creditNote: (id: string) => get<CreditNoteDetail>(`/admin/billing/credit-notes/${id}`),
  issueCreditNote: (body: CreditNoteIn) => post<CreditNoteDetail>("/admin/billing/credit-notes", body),
  applyCreditNote: (id: string, body: { invoice_id: string; amount?: string | null; doc_amount?: string | null }) => post<CreditNoteDetail>(`/admin/billing/credit-notes/${id}/apply`, body),
  sendCreditNote: (id: string) => post<CreditNoteDetail>(`/admin/billing/credit-notes/${id}/send`),
  voidCreditNote: (id: string, reason: string) => post<CreditNoteDetail>(`/admin/billing/credit-notes/${id}/void`, { reason }),
  unapplyCreditNote: (id: string, applicationId: string) => post<CreditNoteDetail>(`/admin/billing/credit-notes/${id}/unapply`, { application_id: applicationId }),
  creditBalances: () => get<CreditBalance[]>("/admin/billing/credit-balances"),
  invoiceCredit: (id: string) => get<InvoiceCredit>(`/admin/billing/invoices/${id}/credit`),
  refund: (id: string, body: RefundIn) => post<InvoiceCredit>(`/admin/billing/invoices/${id}/refund`, body),
  voidInvoice: (id: string, reason: string) => post<InvoiceCredit>(`/admin/billing/invoices/${id}/void`, { reason }),
};

/* ---------- /admin/crm/* routes: leads and owners are served by server/; the rest are not built yet ---------- */

export const crm = {
  dashboard: () => get<CrmDashboard>("/admin/crm/dashboard"),
  owners: () => get<string[]>("/admin/crm/owners"),

  leads: (q: { stage?: LeadStage | ""; source?: string; q?: string; web_status?: string }) => get<Lead[]>("/admin/crm/leads", q),
  lead: (id: string) => get<LeadDetail>(`/admin/crm/leads/${id}`),
  createLead: (body: LeadIn) => post<Lead>("/admin/crm/leads", body),
  updateLead: (id: string, body: Partial<LeadIn>) => patch<Lead>(`/admin/crm/leads/${id}`, body),
  moveLead: (id: string, stage: LeadStage, lost_reason: string | null) =>
    post<Lead>(`/admin/crm/leads/${id}/stage`, { stage, lost_reason }),
  // Puts a lead in another column of the sign-up board by hand ("NEW" = not on the website's sign-up yet).
  setLeadStep: (id: string, step: WebStep | "NEW") => post<Lead>(`/admin/crm/leads/${id}/step`, { step }),
  addActivity: (id: string, kind: LeadActivityKind, body: string) =>
    post<LeadDetail>(`/admin/crm/leads/${id}/activities`, { kind, body }),

  campaigns: (q: { status?: CampaignStatus | ""; channel?: string }) => get<Campaign[]>("/admin/crm/campaigns", q),
  createCampaign: (body: CampaignIn) => post<Campaign>("/admin/crm/campaigns", body),
  campaignAction: (id: string, action: "schedule" | "pause" | "resume" | "cancel") =>
    post<Campaign>(`/admin/crm/campaigns/${id}/${action}`),

  sourceActivities: () => get<SourceActivity[]>("/admin/crm/campaigns/sources"),
  createSourceActivity: (body: SourceActivityIn) => post<SourceActivity>("/admin/crm/campaigns/sources", body),
  updateSourceActivity: (id: string, body: SourceActivityIn) => patch<SourceActivity>(`/admin/crm/campaigns/sources/${id}`, body),
  deleteSourceActivity: (id: string) => del<void>(`/admin/crm/campaigns/sources/${id}`),

  // The 90-day email to leads: settings, ratings, who is due. Served by the CRM's own server.
  csiCheckin: () => get<CsiCheckin>("/admin/crm/csi/checkin"),
  csiCheckinPreview: (lead_id: string) => get<CsiCheckinPreview>("/admin/crm/csi/checkin/preview", { lead_id }),
  setCsiCheckin: (enabled: boolean) => post<CsiCheckin>("/admin/crm/csi/checkin/settings", { enabled }),
  testCsiCheckin: (to: string, lead_id: string) => post<{ status: "sent" | "saved"; detail: string | null }>("/admin/crm/csi/checkin/test", { to, lead_id: lead_id || undefined }),

  meetings: (q: { from?: string; to?: string }) => get<Meeting[]>("/admin/crm/meetings", q),
  createMeeting: (body: MeetingIn) => post<Meeting>("/admin/crm/meetings", body),
  setMeetingStatus: (id: string, status: MeetingStatus, outcome: string | null) =>
    post<Meeting>(`/admin/crm/meetings/${id}/status`, { status, outcome }),
  setMeetingLink: (id: string, meet_link: string | null) => post<Meeting>(`/admin/crm/meetings/${id}/link`, { meet_link }),
  markMeetingJoined: (id: string) => post<Meeting>(`/admin/crm/meetings/${id}/joined`, {}),
  googleStatus: () => get<GoogleCalendarStatus>("/admin/crm/google-calendar/status"),
};

/*
 * Stored subscriptions, plans, addons, coupons and dunning (server/subscriptions.js). A subscription belongs to a client
 * (an onboarding); each term it raises an invoice that shows in Billing and is paid like any onboarding payment.
 */
import type {
  Addon,
  AddonIn,
  Coupon,
  CouponIn,
  DunningSettings,
  PlanFull,
  PlanIn,
  SubClient,
  SubscriptionDetail,
  SubscriptionIn,
  SubscriptionRow,
  SubscriptionSummary,
} from "./subscription-types";

const SUBS = "/admin/subscriptions";
export const subscriptions = {
  summary: () => get<SubscriptionSummary>(SUBS + "/summary"),
  list: (q: { status?: string; q?: string; renewing_within_days?: number; dunning?: boolean }) => get<SubscriptionRow[]>(SUBS, q),
  get: (id: string) => get<SubscriptionDetail>(`${SUBS}/${id}`),
  clients: () => get<SubClient[]>(SUBS + "/clients"),
  create: (body: SubscriptionIn) => post<SubscriptionDetail>(SUBS, body),
  pause: (id: string, body: { reason: string | null; resume_on: string | null }) => post<SubscriptionDetail>(`${SUBS}/${id}/pause`, body),
  resume: (id: string) => post<SubscriptionDetail>(`${SUBS}/${id}/resume`),
  cancel: (id: string, body: { when: "now" | "end_of_term"; reason: string }) => post<SubscriptionDetail>(`${SUBS}/${id}/cancel`, body),
  undoCancel: (id: string) => post<SubscriptionDetail>(`${SUBS}/${id}/undo-cancel`),
  reactivate: (id: string, body: { bill_now: boolean }) => post<SubscriptionDetail>(`${SUBS}/${id}/reactivate`, body),
  renew: (id: string) => post<SubscriptionDetail>(`${SUBS}/${id}/renew`),
  applyCoupon: (id: string, code: string) => post<SubscriptionDetail>(`${SUBS}/${id}/coupon`, { code }),
  removeCoupon: (id: string) => post<SubscriptionDetail>(`${SUBS}/${id}/remove-coupon`),
  addAddon: (id: string, body: { addon_code: string; quantity: number; invoice_now: boolean }) => post<SubscriptionDetail>(`${SUBS}/${id}/addons`, body),
  removeAddon: (id: string, addon_code: string) => post<SubscriptionDetail>(`${SUBS}/${id}/remove-addon`, { addon_code }),

  plans: () => get<PlanFull[]>(SUBS + "/plans"),
  createPlan: (body: PlanIn) => post<PlanFull>(SUBS + "/plans", body),
  updatePlan: (code: string, body: PlanIn) => post<PlanFull>(`${SUBS}/plans/${code}`, body),
  setPlanArchived: (code: string, archived: boolean) => post<PlanFull>(`${SUBS}/plans/${code}/${archived ? "archive" : "unarchive"}`),

  addons: () => get<Addon[]>(SUBS + "/addons"),
  createAddon: (body: AddonIn) => post<Addon>(SUBS + "/addons", body),
  updateAddon: (code: string, body: AddonIn) => post<Addon>(`${SUBS}/addons/${code}`, body),
  setAddonArchived: (code: string, archived: boolean) => post<Addon>(`${SUBS}/addons/${code}/${archived ? "archive" : "unarchive"}`),

  coupons: () => get<Coupon[]>(SUBS + "/coupons"),
  createCoupon: (body: CouponIn) => post<Coupon>(SUBS + "/coupons", body),
  updateCoupon: (code: string, body: CouponIn) => post<Coupon>(`${SUBS}/coupons/${code}`, body),
  setCouponArchived: (code: string, archived: boolean) => post<Coupon>(`${SUBS}/coupons/${code}/${archived ? "archive" : "unarchive"}`),

  dunning: () => get<DunningSettings>(SUBS + "/dunning"),
  saveDunning: (body: Omit<DunningSettings, "updated_at" | "updated_by_name" | "is_default">) => post<DunningSettings>(SUBS + "/dunning", body),
};

/* ---------- customers and items, served by server/customers.js and server/items.js ---------- */

export const customers = {
  list: (q: { status?: MasterStatus | ""; q?: string }) => get<CustomerList>("/admin/customers", q),
  get: (id: string) => get<CustomerDetail>(`/admin/customers/${id}`),
  create: (body: CustomerIn) => post<CustomerDetail>("/admin/customers", body),
  update: (id: string, body: Partial<CustomerIn>) => patch<CustomerDetail>(`/admin/customers/${id}`, body),
  setStatus: (id: string, status: MasterStatus) => post<CustomerDetail>(`/admin/customers/${id}/status`, { status }),
  /** The customer with the lead's email, or a new one made from the lead. */
  fromLead: (lead_id: string) => post<CustomerDetail>("/admin/customers/from-lead", { lead_id }),
};

/** Clients of app.wealthoak.ai and each client's Legal section (server/clients.js asks the app). The agreement PDF comes as a file, with the session's token. */
export const clients = {
  list: (q: { q?: string; subscription?: "active" | "paused" | "expired" | "" }) => get<ClientList>("/admin/clients", q),
  legal: (id: string) => get<ClientLegal>(`/admin/clients/${encodeURIComponent(id)}/legal`),
  agreementPdf: (id: string) => fetchFile(`/admin/clients/${encodeURIComponent(id)}/legal/pdf`),
  /** Emails each client a reminder to sign their agreement. */
  remind: (ids: string[]) => post<{ results: ClientReminder[] }>("/admin/clients/remind", { ids }),
};

export const items = {
  list: (q: { status?: MasterStatus | ""; type?: string; q?: string }) => get<ItemList>("/admin/items", q),
  create: (body: ItemIn) => post<Item>("/admin/items", body),
  update: (id: string, body: Partial<ItemIn>) => patch<Item>(`/admin/items/${id}`, body),
  setStatus: (id: string, status: MasterStatus) => post<Item>(`/admin/items/${id}/status`, { status }),
  /** Marks one plan, or every plan not yet in the catalog, as items. */
  fromPlans: (body: { plan_code: string } | { all: true }) => post<ItemList>("/admin/items/from-plan", body),
};


/* ---------- purchases: /admin/purchases/*, served by server/purchases.js ---------- */

const P = "/admin/purchases";
export const purchases = {
  meta: () => get<PurchaseMeta>(`${P}/meta`),
  summary: () => get<PurchaseSummary>(`${P}/summary`),

  vendors: (q: { status?: string; q?: string; owing?: boolean }) => get<Vendor[]>(`${P}/vendors`, q),
  vendor: (id: string) => get<VendorDetail>(`${P}/vendors/${id}`),
  createVendor: (body: VendorIn) => post<Vendor>(`${P}/vendors`, body),
  updateVendor: (id: string, body: VendorIn) => patch<Vendor>(`${P}/vendors/${id}`, body),

  expenses: (q: { q?: string; vendor_id?: string; account_id?: string; paid_through_id?: string; from?: string; to?: string; billable?: boolean; itc?: boolean; recurring?: boolean; tag?: string }) =>
    get<Expense[]>(`${P}/expenses`, q),
  createExpense: (body: ExpenseIn) => post<Expense>(`${P}/expenses`, body),
  updateExpense: (id: string, body: Partial<ExpenseIn>) => patch<Expense>(`${P}/expenses/${id}`, body),
  deleteExpense: (id: string) => del<void>(`${P}/expenses/${id}`),
  recurringExpenses: (q: { status?: string; q?: string }) => get<RecurringExpense[]>(`${P}/recurring-expenses`, q),
  createRecurringExpense: (body: RecurringExpenseIn) => post<RecurringExpense>(`${P}/recurring-expenses`, body),
  updateRecurringExpense: (id: string, body: Partial<RecurringExpenseIn>) => patch<RecurringExpense>(`${P}/recurring-expenses/${id}`, body),
  deleteRecurringExpense: (id: string) => del<void>(`${P}/recurring-expenses/${id}`),

  bills: (q: { status?: string; q?: string; vendor_id?: string; from?: string; to?: string; recurring?: boolean }) => get<PurchaseBill[]>(`${P}/bills`, q),
  bill: (id: string) => get<PurchaseBillDetail>(`${P}/bills/${id}`),
  createBill: (body: PurchaseBillIn) => post<PurchaseBillDetail>(`${P}/bills`, body),
  updateBill: (id: string, body: Partial<PurchaseBillIn>) => patch<PurchaseBillDetail>(`${P}/bills/${id}`, body),
  voidBill: (id: string) => post<PurchaseBillDetail>(`${P}/bills/${id}/void`),
  recurringBills: (q: { status?: string; q?: string }) => get<RecurringBill[]>(`${P}/recurring-bills`, q),
  createRecurringBill: (body: RecurringBillIn) => post<RecurringBill>(`${P}/recurring-bills`, body),
  updateRecurringBill: (id: string, body: Partial<RecurringBillIn>) => patch<RecurringBill>(`${P}/recurring-bills/${id}`, body),
  deleteRecurringBill: (id: string) => del<void>(`${P}/recurring-bills/${id}`),

  purchaseOrders: (q: { status?: string; q?: string; vendor_id?: string }) => get<PurchaseOrder[]>(`${P}/purchase-orders`, q),
  purchaseOrder: (id: string) => get<PurchaseOrderDetail>(`${P}/purchase-orders/${id}`),
  createPurchaseOrder: (body: PurchaseOrderIn) => post<PurchaseOrderDetail>(`${P}/purchase-orders`, body),
  updatePurchaseOrder: (id: string, body: Partial<PurchaseOrderIn>) => patch<PurchaseOrderDetail>(`${P}/purchase-orders/${id}`, body),
  receivePurchaseOrder: (id: string) => post<PurchaseOrderDetail>(`${P}/purchase-orders/${id}/received`),
  cancelPurchaseOrder: (id: string) => post<PurchaseOrderDetail>(`${P}/purchase-orders/${id}/cancel`),
  convertPurchaseOrder: (id: string, body: { bill_number: string; date?: string; due_date?: string | null; exchange_rate?: string | null }) =>
    post<{ bill: PurchaseBillDetail; purchase_order: PurchaseOrderDetail }>(`${P}/purchase-orders/${id}/convert`, body),

  payments: (q: { q?: string; vendor_id?: string; mode?: string; unused?: boolean }) => get<PaymentMade[]>(`${P}/payments`, q),
  createPayment: (body: PaymentMadeIn) => post<PaymentMade>(`${P}/payments`, body),
  applyPayment: (id: string, body: { bill_id: string; amount?: string | number | null; doc_amount?: string | null }) => post<PaymentMade>(`${P}/payments/${id}/apply`, body),
  voidPayment: (id: string) => post<PaymentMade>(`${P}/payments/${id}/void`),
  unapplyPayment: (id: string, billId: string) => post<PaymentMade>(`${P}/payments/${id}/unapply`, { bill_id: billId }),
  paymentReversals: (id: string) => get<Reversal[]>(`${P}/payments/${id}/reversals`),

  credits: (q: { status?: string; q?: string; vendor_id?: string }) => get<VendorCredit[]>(`${P}/vendor-credits`, q),
  credit: (id: string) => get<VendorCreditDetail>(`${P}/vendor-credits/${id}`),
  createCredit: (body: VendorCreditIn) => post<VendorCreditDetail>(`${P}/vendor-credits`, body),
  applyCredit: (id: string, body: { bill_id: string; amount?: string | number; doc_amount?: string | null }) => post<VendorCreditDetail>(`${P}/vendor-credits/${id}/apply`, body),
  unapplyCredit: (id: string, applicationId: string) => del<VendorCreditDetail>(`${P}/vendor-credits/${id}/applications/${applicationId}`),
  voidCredit: (id: string) => post<VendorCreditDetail>(`${P}/vendor-credits/${id}/void`),
};


/* ---------- accounting (server/accounting*.js): chart of accounts, journals, banking, reports ---------- */

import type {
  AccountingMeta, AccountingSettings, AcctAccount, AcctAccountIn, AcctAccountTransactions, AutoEntry, BalanceSheet, BankAccount, BankPayment, BankTxn,
  BankTxnIn, GstSummary, Journal, JournalIn, JournalSummary, ProfitAndLoss, ReceivablesAging, ReportQuery, StatementImport, TrialBalance,
} from "./types";

export type ReportName = "trial-balance" | "profit-and-loss" | "balance-sheet" | "gst-summary" | "receivables-aging";

/** Fetches a report as CSV and saves it as a file. The download needs the sign-in token, so a plain link cannot do it. */
async function saveCsv(path: string, query: Query) {
  let res: Response;
  try {
    res = await fetch(BASE + withQuery(path, { ...query, format: "csv" }), {
      headers: { "X-Client": "crm", ...orgHeaders(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
      credentials: "include",
    });
  } catch {
    throw new ApiError(0, "The CRM server could not be reached. Check your connection and try again.");
  }
  if (!res.ok) throw new ApiError(res.status, `The CSV could not be made (${res.status}).`);
  const name = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? "report.csv";
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export const accounting = {
  meta: () => get<AccountingMeta>("/admin/accounting/meta"),
  settings: () => get<AccountingSettings>("/admin/accounting/settings"),
  saveSettings: (lock_date: string | null) => post<AccountingSettings>("/admin/accounting/settings", { lock_date }),
  saveInvoiceBasis: (onboarding_invoice_basis: "payment" | "link") => post<AccountingSettings>("/admin/accounting/settings", { onboarding_invoice_basis }),

  accounts: (q: { type?: string; active?: boolean; q?: string }) => get<AcctAccount[]>("/admin/accounting/accounts", q.active === undefined ? q : { ...q, active: String(q.active) }),
  createAccount: (body: AcctAccountIn) => post<AcctAccount>("/admin/accounting/accounts", body),
  updateAccount: (id: string, body: Partial<AcctAccountIn>) => patch<AcctAccount>(`/admin/accounting/accounts/${id}`, body),
  setAccountActive: (id: string, active: boolean) => post<AcctAccount>(`/admin/accounting/accounts/${id}/active`, { active }),
  deleteAccount: (id: string) => del<void>(`/admin/accounting/accounts/${id}`),
  accountTransactions: (id: string, q: { from?: string; to?: string }) => get<AcctAccountTransactions>(`/admin/accounting/accounts/${id}/transactions`, q),

  journals: (q: { status?: string; q?: string }) => get<JournalSummary[]>("/admin/accounting/journals", q),
  journal: (id: string) => get<Journal>(`/admin/accounting/journals/${id}`),
  createJournal: (body: JournalIn) => post<Journal>("/admin/accounting/journals", body),
  updateJournal: (id: string, body: Partial<JournalIn>) => patch<Journal>(`/admin/accounting/journals/${id}`, body),
  publishJournal: (id: string) => post<Journal>(`/admin/accounting/journals/${id}/publish`),
  reverseJournal: (id: string, date: string | null) => post<Journal>(`/admin/accounting/journals/${id}/reverse`, { date }),
  deleteJournal: (id: string) => del<void>(`/admin/accounting/journals/${id}`),
  autoEntries: (q: ReportQuery) => get<AutoEntry[]>("/admin/accounting/auto-entries", q),

  bankAccounts: () => get<BankAccount[]>("/admin/accounting/bank/accounts"),
  createBankAccount: (body: { name: string; type: string; bank_name?: string | null; account_last4?: string | null }) => post<BankAccount>("/admin/accounting/bank/accounts", body),
  transactions: (q: { account_id?: string; status?: string }) => get<BankTxn[]>("/admin/accounting/bank/transactions", q),
  createTransaction: (body: BankTxnIn) => post<BankTxn>("/admin/accounting/bank/transactions", body),
  deleteTransaction: (id: string) => del<void>(`/admin/accounting/bank/transactions/${id}`),
  importStatement: (accountId: string, csv: string) => post<StatementImport>(`/admin/accounting/bank/accounts/${accountId}/import`, { csv }),
  categorize: (id: string, category_account_id: string) => post<BankTxn>(`/admin/accounting/bank/transactions/${id}/categorize`, { category_account_id }),
  candidates: (id: string) => get<BankPayment[]>(`/admin/accounting/bank/transactions/${id}/candidates`),
  match: (id: string, payment_id: string) => post<BankTxn>(`/admin/accounting/bank/transactions/${id}/match`, { payment_id }),
  unmatch: (id: string) => post<BankTxn>(`/admin/accounting/bank/transactions/${id}/unmatch`),
  payments: (q: { unmatched?: boolean }) => get<BankPayment[]>("/admin/accounting/bank/payments", q.unmatched ? { unmatched: "true" } : undefined),

  trialBalance: (q: ReportQuery) => get<TrialBalance>("/admin/accounting/reports/trial-balance", q),
  profitAndLoss: (q: ReportQuery) => get<ProfitAndLoss>("/admin/accounting/reports/profit-and-loss", q),
  balanceSheet: (q: ReportQuery) => get<BalanceSheet>("/admin/accounting/reports/balance-sheet", q),
  gstSummary: (q: ReportQuery) => get<GstSummary>("/admin/accounting/reports/gst-summary", q),
  receivablesAging: (q: ReportQuery) => get<ReceivablesAging>("/admin/accounting/reports/receivables-aging", q),
  downloadReport: (name: ReportName, q: ReportQuery) => saveCsv(`/admin/accounting/reports/${name}`, q),
};

/** For attachments: a request whose body or answer is bytes. Same session and base URL as api(), nothing else different. */
export async function apiBytes(method: string, path: string, body?: BodyInit, headers: Record<string, string> = {}): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(BASE + path, { method, body, headers: { "X-Client": "crm", ...orgHeaders(), ...headers, ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) }, credentials: "include" });
  } catch {
    noteReach(false);
    throw new ApiError(0, "The CRM server could not be reached. Check your connection and try again.");
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    if (res.status >= 502) noteReach(false);
    try {
      const data = (await res.json()) as { detail?: unknown };
      if (typeof data.detail === "string") message = data.detail;
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, message);
  }
  return res;
}
/** Saves a path that answers with a CSV file (the same download as the accounting reports). */
export const saveCsvFile = (path: string, query: Query = {}) => saveCsv(path, query);
/* ---------- sales documents, served by server/sales-docs.js and server/sales-credit.js ---------- */

import type { Recurring, RecurringIn, RecurringRow, SalesCreditIn, SalesDoc, SalesDocIn, SalesDocRow, SalesKind, SalesMeta, SalesPreview } from "./sales-types";

const SALES = "/admin/sales";
const SEGMENT: Record<SalesKind, string> = { estimate: "estimates", sales_order: "sales-orders", invoice: "invoices", proforma: "proformas" };

/** Fetches a file the API serves (a print page, a PDF) with the session's token; the browser cannot send it itself. */
async function fetchFile(path: string): Promise<{ blob: Blob; name: string | null }> {
  let res: Response;
  try {
    res = await fetch(BASE + path, { headers: { "X-Client": "crm", ...orgHeaders(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) }, credentials: "include" });
  } catch {
    throw new ApiError(0, "The CRM server could not be reached. Check your connection and try again.");
  }
  if (!res.ok) {
    // The server's own reason when it gave one (Clients > Legal: the app is not connected, no agreement yet).
    const detail = await res.json().then((d: { detail?: unknown }) => (typeof d.detail === "string" ? d.detail : null)).catch(() => null);
    throw new ApiError(res.status, detail ?? `The document could not be made (${res.status}).`);
  }
  return { blob: await res.blob(), name: /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? null };
}

/** Opens a document's print page in a new tab (the browser prints it, or saves it as PDF). */
export async function openPrintPage(path: string): Promise<void> {
  // The tab is opened first, in the click's own turn, or the browser's pop-up blocker would stop it.
  const tab = window.open("", "_blank");
  try {
    const { blob } = await fetchFile(path + "/print");
    const url = URL.createObjectURL(new Blob([await blob.text()], { type: "text/html" }));
    if (tab) tab.location.href = url;
    else window.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/** Saves a document's PDF. */
export async function downloadPdf(path: string): Promise<void> {
  const { blob, name } = await fetchFile(path + "/pdf");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name ?? "document.pdf";
  a.click();
  URL.revokeObjectURL(url);
}

/** Where a document's print page and PDF are served from, to pass to openPrintPage and downloadPdf. */
export const salesDocPath = (kind: SalesKind, id: string) => `${SALES}/${SEGMENT[kind]}/${id}`;
export const invoicePath = (id: string) => `/admin/billing/invoices/${id}`;
export const creditNotePath = (id: string) => `/admin/billing/credit-notes/${id}`;

export const sales = {
  meta: () => get<SalesMeta>(`${SALES}/meta`),
  list: (kind: SalesKind, q: { status?: string; q?: string; overdue?: boolean; customer_id?: string }) => get<SalesDocRow[]>(`${SALES}/${SEGMENT[kind]}`, q),
  get: (kind: SalesKind, id: string) => get<SalesDoc>(`${SALES}/${SEGMENT[kind]}/${id}`),
  create: (kind: SalesKind, body: SalesDocIn) => post<SalesDoc>(`${SALES}/${SEGMENT[kind]}`, body),
  update: (kind: SalesKind, id: string, body: Partial<SalesDocIn>) => patch<SalesDoc>(`${SALES}/${SEGMENT[kind]}/${id}`, body),
  remove: (kind: SalesKind, id: string) => del<void>(`${SALES}/${SEGMENT[kind]}/${id}`),
  /** issue, send, email, confirm, accept, decline, convert, void, payments */
  act: (kind: SalesKind, id: string, action: string, body?: unknown) => post<SalesDoc>(`${SALES}/${SEGMENT[kind]}/${id}/${action}`, body),
  preview: (body: Partial<SalesDocIn>) => post<SalesPreview>(`${SALES}/preview`, body),
  recurring: () => get<RecurringRow[]>(`${SALES}/recurring`),
  recurringGet: (id: string) => get<Recurring>(`${SALES}/recurring/${id}`),
  recurringCreate: (body: RecurringIn) => post<Recurring>(`${SALES}/recurring`, body),
  recurringUpdate: (id: string, body: Partial<RecurringIn>) => patch<Recurring>(`${SALES}/recurring/${id}`, body),
  recurringAct: (id: string, action: "pause" | "resume" | "stop") => post<Recurring>(`${SALES}/recurring/${id}/${action}`),
  /** A credit note with lines, against a sales invoice or for a customer on its own. */
  issueCredit: (body: SalesCreditIn) => post<CreditNoteDetail>("/admin/billing/credit-notes", body),
};

/* ---------- reports (server/reports.js): the Reports workspace and the Billing dashboard ---------- */

import type { BillingDashboard, ReportResult, ReportsMeta } from "./reports-types";

export const reports = {
  meta: () => get<ReportsMeta>("/admin/reports/meta"),
  dashboard: (q: Record<string, string>) => get<BillingDashboard>("/admin/reports/dashboard", q),
  run: (key: string, q: Record<string, string>) => get<ReportResult>(`/admin/reports/run/${key}`, q),
  download: (key: string, q: Record<string, string>) => saveCsv(`/admin/reports/run/${key}`, q),
  favourite: (key: string, favourite: boolean) => post<{ favourites: string[] }>("/admin/reports/favourites", { key, favourite }),
};

/* ---------- inventory and locations (server/inventory*.js, server/locations.js) ---------- */

import type {
  Adjustment, AdjustmentIn, Challan, ChallanIn, InventoryMeta, InventorySettings, ItemStock, Location, LocationIn, LocationList, MovementRow, Package, PackageList, Receive, ReceiveList, StockList, Transfer, TransferIn,
} from "./inventory-types";

const INV = "/admin/inventory";

export const locations = {
  list: () => get<LocationList>("/admin/locations"),
  create: (body: LocationIn) => post<Location>("/admin/locations", body),
  update: (id: string, body: LocationIn) => patch<Location>(`/admin/locations/${id}`, body),
  setStatus: (id: string, status: "active" | "inactive") => post<Location>(`/admin/locations/${id}/status`, { status }),
  makeDefault: (id: string) => post<Location>(`/admin/locations/${id}/default`),
};

export const inventory = {
  meta: () => get<InventoryMeta>(`${INV}/meta`),
  saveSettings: (body: Partial<Pick<InventorySettings, "enabled" | "valuation" | "negative" | "stock_out">>) => post<InventorySettings>(`${INV}/settings`, body),
  stock: (q: { location_id?: string; q?: string; as_of?: string }) => get<StockList>(`${INV}/stock`, q),
  item: (id: string) => get<ItemStock>(`${INV}/items/${id}`),
  history: (id: string, q: { from?: string; to?: string; location_id?: string }) => get<{ rows: MovementRow[] }>(`${INV}/items/${id}/history`, q),
  setOpening: (id: string, body: { as_of: string; rows: { location_id: string; quantity: string; rate: string }[] }) => post<ItemStock>(`${INV}/items/${id}/opening`, body),
  adjustments: () => get<Adjustment[]>(`${INV}/adjustments`),
  adjust: (body: AdjustmentIn) => post<Adjustment>(`${INV}/adjustments`, body),
  voidAdjustment: (id: string, reason: string) => post<Adjustment>(`${INV}/adjustments/${id}/void`, { reason }),
  transfers: () => get<Transfer[]>(`${INV}/transfers`),
  transfer: (body: TransferIn) => post<Transfer>(`${INV}/transfers`, body),
  transferAct: (id: string, action: "send" | "received" | "void", body: { date?: string; reason?: string } = {}) => post<Transfer>(`${INV}/transfers/${id}/${action}`, body),
  challans: () => get<Challan[]>(`${INV}/challans`),
  challan: (body: ChallanIn) => post<Challan>(`${INV}/challans`, body),
  updateChallan: (id: string, body: Partial<ChallanIn>) => patch<Challan>(`${INV}/challans/${id}`, body),
  deleteChallan: (id: string) => del<null>(`${INV}/challans/${id}`),
  challanAct: (id: string, action: "issue" | "void") => post<Challan>(`${INV}/challans/${id}/${action}`),
  challanStatus: (id: string, status: "delivered" | "returned", date?: string) => post<Challan>(`${INV}/challans/${id}/status`, { status, date }),
  challanInvoice: (id: string) => post<{ challan: Challan; invoice_id: string }>(`${INV}/challans/${id}/convert`),
  packages: () => get<PackageList>(`${INV}/packages`),
  pack: (body: { invoice_id: string; date?: string; carrier?: string | null; tracking_number?: string | null; lines?: { line_id: string; quantity: string }[] }) => post<Package>(`${INV}/packages`, body),
  packageStatus: (id: string, body: { status: "shipped" | "delivered"; date?: string; carrier?: string | null; tracking_number?: string | null }) => post<Package>(`${INV}/packages/${id}/status`, body),
  voidPackage: (id: string) => post<Package>(`${INV}/packages/${id}/void`),
  receives: () => get<ReceiveList>(`${INV}/receives`),
  receive: (body: { po_id: string; date?: string; location_id?: string | null; lines?: { po_line_id: string; quantity: string }[] }) => post<Receive>(`${INV}/receives`, body),
  voidReceive: (id: string) => post<Receive>(`${INV}/receives/${id}/void`),
};
